import { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Divider,
  IconButton,
  List,
  SwipeableDrawer,
  Link,
  Typography,
  Grid
} from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import theme from 'App.theme';
import { Icon } from 'components/Icon';
import { useContractOrAgreement } from 'hooks/useContractOrAgreement';
import { RootDispatch, RootState } from 'storage/store';
import { IsLoggedIn } from 'utils/user';
import { menuClickEvent } from 'utils/events';
import sendEvent from 'utils/sendEvent';
import { Index } from 'utils/consts';
import { UserInfo } from '../styled/UserInfo';
import { getMenuFooterList, getMenuList } from '../utils';
import AppBarButtons from './AppBarButtons';
import PWAButton from './PWAButton';
import MenuItemNavLink from '../styled/MenuItemNavLink';
import { MenuVisibilityContext } from '../utils/menuVisibilityContext';
import { colors } from '../../Colors';
import BadgeAndMenu from './Notifications/BadgeAndMenu';

interface IMobileMenu {
  username: string;
}

const MobileMenu = ({ username }: IMobileMenu) => {
  const { t } = useTranslation();
  const { menuVisible, setVisible, setNotificationsVisible } = useContext(
    MenuVisibilityContext
  );
  const contractsToggled = useSelector(
    (state: RootState) => state.application?.contractDashboardFeatureToggle
  );
  const { agreements } = useContractOrAgreement();
  const isUserProfileAvailable =
    !contractsToggled || Boolean(agreements?.length ?? 0);

  const { modals } = useDispatch<RootDispatch>();
  const menuList = getMenuList(t, isUserProfileAvailable);
  const menuFooterList = getMenuFooterList(t, setVisible!, modals);
  const { showButton } = useSelector((state: RootState) => state.pwa);
  const isUserLoggedIn = IsLoggedIn();

  const mobileMenuAction = (isMenuVisible: boolean, text: string) => {
    setVisible!(isMenuVisible);
    setNotificationsVisible!(false);
    sendEvent({
      labelText: text,
      ...menuClickEvent
    });
  };

  return (
    <Grid>
      {isUserLoggedIn ? (
        <Box sx={{ display: 'inline-block' }}>
          <BadgeAndMenu />
        </Box>
      ) : null}
      <IconButton onClick={() => mobileMenuAction(!menuVisible, 'mobile menu')}>
        <Icon icon={menuVisible ? 'close' : 'menu'} color="primary" />
      </IconButton>
      <SwipeableDrawer
        disableBackdropTransition
        anchor="top"
        open={menuVisible}
        onClose={() => setVisible!(false)}
        onOpen={() => setVisible!(true)}
        slotProps={{
          backdrop: {
            invisible: true
          }
        }}
        disableScrollLock
      >
        <Box
          sx={{
            padding: {
              xs: '24px 24px 70px',
              md: '36px 49px'
            }
          }}
        >
          {username && (
            <UserInfo
              variant="body1"
              sx={{
                fontWeight: 700,
                margin: '12px 0',
                color: 'secondary.main'
              }}
            >
              <Icon icon="user" color="secondary" />
              {username}
            </UserInfo>
          )}
          {[menuList.primary, menuList.secondary].map(
            (group, groupIndex, list) => (
              <List
                sx={{
                  marginTop: '5px',
                  marginBottom:
                    groupIndex === Index.zero && isUserLoggedIn ? '20px' : ''
                }}
                key={group.id}
              >
                {group.items
                  .filter(
                    (item) =>
                      (isUserLoggedIn ? true : !item.loggedOnly) &&
                      item.isVisible
                  )
                  .map(({ href, text }, index) => (
                    <Fragment key={text}>
                      <ListItemButton disableGutters dense>
                        <Typography
                          component={MenuItemNavLink}
                          to={href}
                          onClick={() => mobileMenuAction(false, text)}
                          variant="h4"
                          color="primary"
                          sx={{
                            fontWeight: '900'
                          }}
                        >
                          {text}
                        </Typography>
                      </ListItemButton>
                      {index < list.length && isUserLoggedIn && (
                        <Divider
                          sx={{
                            borderWidth: '1px',
                            borderColor: colors.green.midTeal.value
                          }}
                        />
                      )}
                    </Fragment>
                  ))}
              </List>
            )
          )}
          <Grid
            container
            sx={{
              width: '190px',
              marginTop: isUserLoggedIn ? '19px' : '7px'
            }}
          >
            <AppBarButtons />
          </Grid>
          <Grid
            container
            sx={{
              marginTop: '35px',
              maxWidth: '400px',
              width: { xs: 'auto' }
            }}
          >
            {showButton && <PWAButton />}
          </Grid>
          <Grid
            container
            sx={{
              flexDirection: 'column',
              marginTop: isUserLoggedIn ? '38px' : '34px'
            }}
          >
            {menuFooterList.map(({ text, href, onClick }, index, list) => (
              <Grid
                key={text}
                sx={{
                  marginBottom: index < list.length ? '29px' : '0'
                }}
              >
                {href ? (
                  <Typography
                    variant="body1"
                    color="secondary"
                    component={MenuItemNavLink}
                    to={href}
                    onClick={() => mobileMenuAction(false, text)}
                    sx={{
                      fontWeight: '700'
                    }}
                  >
                    {text}
                  </Typography>
                ) : (
                  <Link
                    href={href}
                    onClick={onClick}
                    sx={{
                      color: theme.palette.secondary.main,
                      cursor: 'pointer'
                    }}
                  >
                    {text}
                  </Link>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      </SwipeableDrawer>
    </Grid>
  );
};

export default MobileMenu;
